import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  HostexProperty,
  LinkedApartment,
  PmsConnection,
  disconnectHostex,
  getHostexProperties,
  getLinkedApartments,
  reopenSecretEnrolment,
  testHostexConnection,
} from '../../api.channelManager';
import MessageTemplateEditor from '../MessageTemplateEditor';
import './channelManager.css';

interface Props {
  connection: PmsConnection;
  onBack: () => void;
  onChanged: (conn: PmsConnection) => void;
  onDisconnect: () => void;
}

type Tab = 'info' | 'messages';

const ConnectionDetailView: React.FC<Props> = ({ connection, onBack, onChanged, onDisconnect }) => {
  const [tab, setTab] = useState<Tab>('info');
  const [testing, setTesting] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  // Read-only apartment/property overview. `properties` (the denominator) is only fetched while
  // the connection is enabled — a disabled token would just fail the call.
  const [linkedApartments, setLinkedApartments] = useState<LinkedApartment[] | null>(null);
  const [properties, setProperties] = useState<HostexProperty[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    getLinkedApartments()
      .then((rows) => { if (!cancelled) setLinkedApartments(rows); })
      .catch(() => { if (!cancelled) setLinkedApartments([]); });
    if (connection.enabled) {
      getHostexProperties()
        .then((rows) => { if (!cancelled) setProperties(rows); })
        .catch(() => { if (!cancelled) setProperties(null); });
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearMessages = () => {
    setMessage(null);
    setError(null);
  };

  const handleTest = async () => {
    clearMessages();
    setTesting(true);
    try {
      const next = await testHostexConnection();
      onChanged(next);
      setMessage(next.enabled ? 'Connection is healthy.' : next.lastError || 'Connection failed.');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleReopen = async () => {
    clearMessages();
    setReopening(true);
    try {
      const next = await reopenSecretEnrolment();
      onChanged(next);
      setMessage('Verification window reopened — the next webhook from Hostex will be adopted.');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to reopen');
    } finally {
      setReopening(false);
    }
  };

  const handleDisconnect = async () => {
    clearMessages();
    setDisconnecting(true);
    try {
      await disconnectHostex();
      onDisconnect();
    } catch (err: any) {
      // 409 when apartments still use it — the message explains what to do first.
      setError(err.response?.data?.message || err.message || 'Failed to disconnect');
      setConfirmingDisconnect(false);
    } finally {
      setDisconnecting(false);
    }
  };

  const connected = connection.enabled && connection.hasWebhookSecret;

  return (
    <div>
      <div className="cm-detail-header">
        <button type="button" className="app-btn app-btn-blue app-btn-compact" onClick={onBack}>
          ← Back
        </button>
        <div style={{ fontWeight: 700, fontSize: '1.15rem', color: 'var(--text-primary)' }}>Hostex</div>
        <span className={`cm-pill ${connected ? 'cm-pill-success' : 'cm-pill-warn'}`} style={{ marginLeft: 'auto' }}>
          {connected ? 'Connected' : 'Setup incomplete'}
        </span>
      </div>

      <div className="cm-tabs">
        <button type="button" className={`cm-tab ${tab === 'info' ? 'active' : ''}`} onClick={() => setTab('info')}>
          Info
        </button>
        <button
          type="button"
          className={`cm-tab ${tab === 'messages' ? 'active' : ''}`}
          onClick={() => setTab('messages')}
        >
          Messages
        </button>
      </div>

      {tab === 'info' && (
        <div>
          {message && (
            <div
              style={{
                padding: '10px 14px',
                marginBottom: 16,
                background: '#f0fdf4',
                border: '1px solid #86efac',
                borderRadius: 8,
                color: '#166534',
                fontSize: '0.87rem',
                fontWeight: 600,
              }}
            >
              {message}
            </div>
          )}
          {error && (
            <div
              style={{
                padding: '10px 14px',
                marginBottom: 16,
                background: '#fef2f2',
                border: '1px solid #fca5a5',
                borderRadius: 8,
                color: '#991b1b',
                fontSize: '0.87rem',
                fontWeight: 600,
              }}
            >
              ⚠️ {error}
            </div>
          )}

          <div
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'flex-start',
              padding: '11px 14px',
              marginBottom: 16,
              background: connection.enabled ? '#f0fdf4' : '#fef2f2',
              border: `1px solid ${connection.enabled ? '#86efac' : '#fca5a5'}`,
              borderRadius: 8,
              color: connection.enabled ? '#166534' : '#991b1b',
              fontSize: '0.86rem',
            }}
          >
            <span>{connection.enabled ? '✅' : '⚠️'}</span>
            <div>
              <div style={{ fontWeight: 600 }}>
                {connection.enabled
                  ? `Connected · ${linkedSummary(linkedApartments, properties, connection.linkedApartments)}`
                  : 'Disabled'}
              </div>
              <div style={{ fontSize: '0.8rem', marginTop: 2 }}>
                {connection.lastError
                  ? connection.lastError
                  : `Last event: ${connection.lastWebhookAt ? new Date(connection.lastWebhookAt).toLocaleString() : 'never'}`}
              </div>
            </div>
          </div>

          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 5 }}>
              Linked apartments
            </div>
            {linkedApartments === null ? (
              <div style={{ fontSize: '0.86rem', color: 'var(--text-muted)' }}>Loading…</div>
            ) : linkedApartments.length === 0 ? (
              <div style={{ fontSize: '0.86rem', color: 'var(--text-muted)' }}>
                No apartments linked yet.
              </div>
            ) : (
              <div className="cm-linked-list">
                {linkedApartments.map((a) => (
                  <div key={a.apartmentId} className="cm-linked-row">
                    <Link
                      to={`/apartments/${a.apartmentId}?tab=calendar`}
                      className="cm-linked-apartment"
                    >
                      {a.apartmentName}
                    </Link>
                    <span className="cm-linked-arrow">→</span>
                    <span className="cm-linked-property">
                      {a.externalPropertyName || a.externalPropertyId || '—'}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 8 }}>
              This list is read-only. To link an apartment, open it in <strong>Apartments</strong>, then{' '}
              <strong>Calendar sync</strong> → <strong>Channel manager</strong>.
            </div>
          </div>

          {connection.enabled && !connection.canWrite && (
            <div
              style={{
                padding: '10px 14px',
                marginBottom: 16,
                background: '#fef9c3',
                border: '1px solid #fde047',
                borderRadius: 8,
                color: '#713f12',
                fontSize: '0.85rem',
              }}
            >
              ✍️ This token is read-only — reservations sync fine, but guests can't be messaged. Reconnect with a
              writable key to enable messaging.
            </div>
          )}

          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 5 }}>
              Webhook verification
            </div>
            {connection.hasWebhookSecret ? (
              <div style={{ fontSize: '0.86rem', color: '#166534' }}>
                ✅ Verified — incoming events are checked against Hostex's secret.
              </div>
            ) : connection.secretEnrolmentOpen ? (
              <div style={{ fontSize: '0.86rem', color: '#713f12' }}>
                ⏳ Waiting for the first webhook from Hostex. It will be picked up automatically.
              </div>
            ) : (
              <div style={{ fontSize: '0.86rem', color: '#991b1b' }}>
                ⚠️ No webhook arrived in time. Events are currently accepted on the secret address alone — reopen
                below to verify properly.
              </div>
            )}
            {!connection.hasWebhookSecret && (
              <button
                type="button"
                className="app-btn app-btn-blue app-btn-compact"
                style={{ marginTop: 10 }}
                disabled={reopening}
                onClick={handleReopen}
              >
                {reopening ? 'Reopening…' : 'Reopen verification window'}
              </button>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="app-btn app-btn-blue app-btn-standard"
              disabled={testing}
              onClick={handleTest}
            >
              {testing ? 'Testing…' : 'Test connection'}
            </button>
            {confirmingDisconnect ? (
              <>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)', alignSelf: 'center' }}>
                  Remove the connection? Linked apartments will stop syncing.
                </span>
                <button
                  type="button"
                  className="app-btn app-btn-red app-btn-standard"
                  disabled={disconnecting}
                  onClick={handleDisconnect}
                >
                  {disconnecting ? 'Disconnecting…' : 'Yes, disconnect'}
                </button>
                <button
                  type="button"
                  className="app-btn app-btn-blue app-btn-standard"
                  disabled={disconnecting}
                  onClick={() => setConfirmingDisconnect(false)}
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                className="app-btn app-btn-red app-btn-standard"
                onClick={() => setConfirmingDisconnect(true)}
              >
                Disconnect
              </button>
            )}
          </div>
        </div>
      )}

      {tab === 'messages' && <MessageTemplateEditor />}
    </div>
  );
};

/**
 * "2/3 apartments linked to Guestbook" once the property count is known; falls back to the plain
 * count (no denominator) while properties haven't loaded yet, or couldn't — a disabled connection
 * never fetches them, and a rejected token fails the fetch outright.
 */
function linkedSummary(
  linkedApartments: LinkedApartment[] | null,
  properties: HostexProperty[] | null,
  fallbackCount: number
): string {
  const linked = linkedApartments === null ? fallbackCount : linkedApartments.length;
  if (properties !== null) {
    return `${linked}/${properties.length} apartments linked to Guestbook`;
  }
  return `${linked} apartment${linked === 1 ? '' : 's'} linked`;
}

export default ConnectionDetailView;
